import type { ParsedUrlQuery } from 'querystring'

import type {
    Product,
    ProductSort,
    StatusFilter
} from '@/types'

import { getStockStatus, normalizeText } from '@/lib/format'
import { PRODUCT_SORTS, STATUS_FILTERS } from '@/constants/inventory'

export type InventoryFilters = {
    search: string
    category: string
    status: StatusFilter
    sort: ProductSort
}

export const DEFAULT_FILTERS: InventoryFilters = {
    search: '',
    category: '',
    status: 'todos',
    sort: 'nombre'
}

export const UNCATEGORIZED = 'sin-categoria'

const readText = (value: string | string[] | undefined): string => {
    if (typeof value !== 'string') return ''

    return value.slice(0, 100)
}

export const readInventoryFilters = (query: ParsedUrlQuery): InventoryFilters => {
    const status = readText(query.estado) as StatusFilter
    const sort = readText(query.orden) as ProductSort

    return {
        search: readText(query.q),
        category: readText(query.categoria),
        status: STATUS_FILTERS.some(option => option.value === status) ? status : DEFAULT_FILTERS.status,
        sort: PRODUCT_SORTS.some(option => option.value === sort) ? sort : DEFAULT_FILTERS.sort
    }
}

export const toInventoryQuery = (filters: InventoryFilters): Record<string, string> => {
    const query: Record<string, string> = {}

    if (filters.search.trim()) query.q = filters.search.trim()
    if (filters.category) query.categoria = filters.category
    if (filters.status !== DEFAULT_FILTERS.status) query.estado = filters.status
    if (filters.sort !== DEFAULT_FILTERS.sort) query.orden = filters.sort

    return query
}

export const matchesSearch = (product: Product, search: string): boolean => {
    const term = normalizeText(search)

    if (!term) return true

    return [product.name, product.sku, product.supplier, product.location, product.categoryName]
        .some(value => normalizeText(value).includes(term))
}

export const matchesCategory = (product: Product, category: string): boolean => {
    if (!category) return true
    if (category === UNCATEGORIZED) return !product.categoryId

    return product.categoryId === category
}

export const matchesStatus = (product: Product, status: StatusFilter): boolean => {
    const stockStatus = getStockStatus(product.quantity, product.minStock)

    if (status === 'reponer') return stockStatus !== 'ok'
    if (status === 'agotados') return stockStatus === 'out'
    if (status === 'disponibles') return stockStatus === 'ok'

    return true
}

const byName = (a: Product, b: Product): number => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })

export const sortProducts = (products: Product[], sort: ProductSort): Product[] => {
    const sorted = [...products]

    if (sort === 'stock-asc') return sorted.sort((a, b) => a.quantity - b.quantity || byName(a, b))
    if (sort === 'stock-desc') return sorted.sort((a, b) => b.quantity - a.quantity || byName(a, b))
    if (sort === 'valor') return sorted.sort((a, b) => b.quantity * b.cost - a.quantity * a.cost || byName(a, b))
    if (sort === 'recientes') return sorted.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || byName(a, b))

    return sorted.sort(byName)
}
